// Search service with smart ranking algorithm
import { normalizeText, getQueryWords, calculateFieldScore, calculateRecencyBonus, highlightMatches } from '@/utils/searchUtils';
/**
 * Default search options
 */
const DEFAULT_OPTIONS = {
    minScore: 0,
    maxResults: 15,
    includeCategories: true,
    includeProjects: true,
    caseSensitive: false
};
/**
 * Main search function - searches sessions with smart ranking
 *
 * @param query - Search query string
 * @param sessions - Array of sessions to search
 * @param options - Optional search configuration
 * @returns Sorted array of search results (max 15 by default)
 */
export function searchSessions(query, sessions, options = {}) {
    // Merge with default options
    const opts = { ...DEFAULT_OPTIONS, ...options };
    // Validate input
    if (!query || query.trim().length < 2) {
        return [];
    }
    if (!sessions || sessions.length === 0) {
        return [];
    }
    const normalizedQuery = normalizeText(query);
    const queryWords = getQueryWords(query);
    const results = [];
    // Search through ALL sessions to find best matches
    for (const session of sessions) {
        const sessionResult = scoreSession(session, normalizedQuery, queryWords);
        if (sessionResult && sessionResult.score >= opts.minScore) {
            results.push(sessionResult);
        }
    }
    // Sort by score (descending) to get best matches first
    results.sort((a, b) => b.score - a.score);
    // Store total count before slicing
    const totalMatches = results.length;
    // Take top N results (max 15) from all matches
    const topResults = results.slice(0, opts.maxResults);
    // Add highlighting to top results and attach total count metadata
    const highlightedResults = topResults.map(result => addHighlighting(result, query));
    // Attach total matches count as a property on the array
    highlightedResults._totalMatches = totalMatches;
    return highlightedResults;
}
/**
 * Score a single session against the query
 */
function scoreSession(session, normalizedQuery, queryWords) {
    const project = session.project || '';
    const category = session.category || '';
    const sessionName = session.name || '';
    const sessionId = session.id || '';
    const projectId = session.projectId || '';
    const categoryId = session.categoryId || '';
    // Calculate scores for each field
    const projectScore = calculateFieldScore(project, normalizedQuery, 5);
    const categoryScore = calculateFieldScore(category, normalizedQuery, 10);
    const sessionScore = calculateFieldScore(sessionName, normalizedQuery, 15);
    // Search UIDs with high priority (+100 for exact match)
    let uidScore = 0;
    let matchedUid = '';
    if (normalizedQuery.includes('s_') && sessionId.toLowerCase().includes(normalizedQuery)) {
        uidScore = 100;
        matchedUid = sessionId;
    }
    else if (normalizedQuery.includes('c_') && categoryId.toLowerCase().includes(normalizedQuery)) {
        uidScore = 100;
        matchedUid = categoryId;
    }
    else if (normalizedQuery.includes('p_') && projectId.toLowerCase().includes(normalizedQuery)) {
        uidScore = 100;
        matchedUid = projectId;
    }
    // Total base score
    let totalScore = projectScore + categoryScore + sessionScore + uidScore;
    // If no matches at all, skip this session
    if (totalScore === 0) {
        return null;
    }
    // Add recency bonus
    const recencyBonus = calculateRecencyBonus(session.createdAt);
    totalScore += recencyBonus;
    // Determine which fields matched
    const matchedFields = [];
    if (projectScore > 0)
        matchedFields.push('project');
    if (categoryScore > 0)
        matchedFields.push('category');
    if (sessionScore > 0)
        matchedFields.push('session');
    if (uidScore > 0)
        matchedFields.push('uid');
    // Determine which UID to display
    let displayUid = sessionId;
    if (matchedUid === categoryId) {
        displayUid = categoryId;
    }
    else if (matchedUid === projectId) {
        displayUid = projectId;
    }
    return {
        type: 'session',
        score: totalScore,
        session: session,
        id: session.id,
        sessionName: sessionName,
        project: project,
        category: category,
        createdAt: session.createdAt,
        projectId: projectId,
        categoryId: categoryId,
        displayUid: displayUid,
        matchedFields
    };
}
/**
 * Add highlighted text to search result
 */
function addHighlighting(result, query) {
    return {
        ...result,
        highlightedText: {
            project: result.matchedFields.includes('project')
                ? highlightMatches(result.project, query)
                : result.project,
            category: result.matchedFields.includes('category')
                ? highlightMatches(result.category || '', query)
                : result.category,
            session: result.matchedFields.includes('session')
                ? highlightMatches(result.sessionName || '', query)
                : result.sessionName,
            uid: result.matchedFields.includes('uid')
                ? highlightMatches(result.displayUid || '', query)
                : result.displayUid
        }
    };
}
/**
 * Search for category matches (for future Phase 4)
 * Returns results where category name matches query
 */
export function searchCategories(query, sessions, maxResults = 5) {
    const normalizedQuery = normalizeText(query);
    const categoryMap = new Map();
    for (const session of sessions) {
        const categoryKey = `${session.project}|${session.category}`;
        if (categoryMap.has(categoryKey))
            continue;
        const categoryScore = calculateFieldScore(session.category || '', normalizedQuery, 10);
        if (categoryScore > 0) {
            categoryMap.set(categoryKey, {
                type: 'category',
                score: categoryScore,
                project: session.project || '',
                category: session.category || '',
                categoryId: session.categoryId || '',
                projectId: session.projectId || '',
                displayUid: session.categoryId || '',
                matchedFields: ['category']
            });
        }
    }
    return Array.from(categoryMap.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, maxResults);
}
/**
 * Search for project matches (for future Phase 4)
 * Returns results where project name matches query
 */
export function searchProjects(query, sessions, maxResults = 5) {
    const normalizedQuery = normalizeText(query);
    const projectMap = new Map();
    for (const session of sessions) {
        if (projectMap.has(session.project))
            continue;
        const projectScore = calculateFieldScore(session.project || '', normalizedQuery, 5);
        if (projectScore > 0) {
            projectMap.set(session.project, {
                type: 'project',
                score: projectScore,
                project: session.project || '',
                projectId: session.projectId || '',
                displayUid: session.projectId || '',
                matchedFields: ['project']
            });
        }
    }
    return Array.from(projectMap.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, maxResults);
}
/**
 * Combined search (sessions + categories + projects)
 * Returns sessions first (max 15), then P/C results (max 5 combined)
 * P/C results always shown when there are matches (partial or full)
 */
export function searchAll(query, sessions, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    // Get session results with total matches count
    const sessionResults = searchSessions(query, sessions, {
        ...opts,
        maxResults: 999999 // Get ALL session matches first
    });
    const totalSessionMatches = sessionResults._totalMatches || sessionResults.length;
    // Get category and project results (these are always small sets)
    const categoryResults = opts.includeCategories
        ? searchCategories(query, sessions, 999999)
        : [];
    const projectResults = opts.includeProjects
        ? searchProjects(query, sessions, 999999)
        : [];
    // Sort sessions by score and take top 15
    const sortedSessions = sessionResults.sort((a, b) => b.score - a.score);
    const topSessions = sortedSessions.slice(0, opts.maxResults);
    // Combine P/C results and sort them together by score, then take top 5
    const sortedPCResults = [...categoryResults, ...projectResults]
        .sort((a, b) => b.score - a.score)
        .slice(0, 5); // Max 5 P/C results
    // Combine: sessions first, then P/C results (with spacer between)
    const combinedResults = [...topSessions, ...sortedPCResults];
    // Store total count
    const totalMatches = sessionResults.length + categoryResults.length + projectResults.length;
    // Add highlighting to all results
    const highlightedResults = combinedResults.map(result => addHighlighting(result, query));
    // Attach total matches count as metadata
    highlightedResults._totalMatches = totalMatches;
    highlightedResults._sessionMatches = totalSessionMatches;
    highlightedResults._categoryMatches = categoryResults.length;
    highlightedResults._projectMatches = projectResults.length;
    highlightedResults._sessionResultsCount = topSessions.length; // For spacer positioning
    return highlightedResults;
}
